class CartDrawer extends ModalDrawer {
	constructor() {
		super();

		// The generic slide/overlay/Esc/focus-trap/scroll-lock lifecycle is owned
		// by the nested <modal-drawer> that drawer-shell emits (see cart-drawer.liquid).
		// This element keeps the cart-specific concerns: the header trigger, the
		// AJAX section render and the cart-note accessibility wiring.
		this.setHeaderCartIconAccessibility();

		// The close button and overlay live inside the panel, which is replaced
		// wholesale on every AJAX cart re-render. Bind their dismiss via delegation
		// on this persistent element (the modern replacement for Madrid's inline
		// `onclick="…close()"`) so it survives `renderContents` / quantity updates.
		this.addEventListener("click", (event) => {
			if (event.target.closest(".drawer__overlay, .drawer__close")) {
				event.preventDefault();
				this.close();
			}
		});
	}

	// The live <modal-drawer> panel — re-queried each call because renderContents
	// recreates it when it replaces #CartDrawer's contents.
	get modalDrawer() {
		return this.querySelector("modal-drawer");
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		if (!cartLink) return;
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");

		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});

		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		this.setCartNoteAccessibility();

		this.modalDrawer?.open(triggeredBy);
	}

	// The cart-note <summary> is inside the panel, so it is a fresh element after
	// every AJAX re-render - re-wire it whenever the drawer is shown.
	setCartNoteAccessibility() {
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
	}

	close() {
		this.modalDrawer?.close();
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;

		// QA #140 - "weird flash after add to cart". Adding a second time while the
		// drawer is already open used to replace the whole of #CartDrawer, which
		// destroys the live <modal-drawer class="drawer animate active"> and swaps in
		// a fresh, closed one: the panel snapped off-screen (translateX(100%),
		// visibility:hidden) and then re-slid back in over 600ms. Measured on the
		// storefront: open panel at translateX(0) -> translateX(375px) within one
		// frame of the swap, then a full re-slide. Rebinding was also the reason the
		// re-open was needed at all.
		//
		// So when the panel is already open, swap only the panel's INNER content and
		// leave the <modal-drawer> host (and its .active class / transform) untouched
		// - no teardown, no re-slide, no flash. The closed case keeps the original
		// whole-container replace + open(), which is already flash-free because the
		// panel is off-screen while it is rewritten.
		const isOpen = this.modalDrawer?.classList.contains("active");

		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			if (!sectionElement) return;

			if (isOpen && section.id === "cart-drawer") {
				this.renderOpenPanel(parsedState.sections[section.id]);
				return;
			}

			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		if (isOpen) {
			// Same element, new contents: only the note wiring needs re-binding, and
			// the focus trap has to be refreshed because it cached the old children.
			this.setCartNoteAccessibility();
			trapFocus(this, this.modalDrawer.drawerInner);
			return;
		}

		// Replacing #CartDrawer's contents rebuilds the <modal-drawer>, so re-open
		// it (fresh element, fresh lifecycle) to slide the refreshed cart in.
		setTimeout(() => {
			this.open();
		});
	}

	// Update an already-open panel in place: copy the refreshed header and body
	// from the section payload into the live .drawer__inner, so the host
	// <modal-drawer> (and therefore the open transform) is preserved.
	renderOpenPanel(sectionHTML) {
		const liveInner = this.modalDrawer?.querySelector(".drawer__inner");
		const nextInner = this.getSectionDOM(sectionHTML, "#CartDrawer")?.querySelector(
			".drawer__inner"
		);
		if (!liveInner || !nextInner) return;

		liveInner.innerHTML = nextInner.innerHTML;
		liveInner.className = nextInner.className;
		this.modalDrawer.drawerInner = liveInner;
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
